import { createAxiosInstance } from '../utils/axiosConfig';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8083/api';

// Create axios instance with default config and interceptors
const productAPI = createAxiosInstance({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Response interceptor for error handling
productAPI.interceptors.response.use(
  (response) => response,
  (error) => {
    const errorMessage = error.response?.data?.message || error.message || 'An error occurred';
    console.error('Product API Error:', errorMessage);
    return Promise.reject(errorMessage);
  }
);

export const productService = {
  // Featured Products Management
  getFeaturedProducts: async (params = {}) => {
    try {
      const { page, size, limit } = params;
      let url = '/products/featured';
      
      if (page !== undefined && size !== undefined) {
        url += `?page=${page}&size=${size}`;
      } else if (limit) {
        url += `?limit=${limit}`;
      }
      
      const response = await productAPI.get(url);
      return response.data;
    } catch (error) {
      throw new Error(error || 'Failed to fetch featured products');
    }
  },

  setProductFeatured: async (productId, featured = true) => {
    try {
      const method = featured ? 'POST' : 'DELETE';
      const response = await productAPI({
        method,
        url: `/products/${productId}/featured`,
      });
      return response.data;
    } catch (error) {
      throw new Error(error || `Failed to ${featured ? 'add' : 'remove'} product ${featured ? 'to' : 'from'} featured`);
    }
  },

  toggleProductFeatured: async (productId) => {
    try {
      const response = await productAPI.post(`/products/${productId}/toggle-featured`);
      return response.data;
    } catch (error) {
      throw new Error(error || 'Failed to toggle product featured status');
    }
  },

  // General Product Operations
  getAllProducts: async (params = {}) => {
    try {
      const { page = 0, size = 10, status, category, search } = params;
      const queryParams = { page, size };
      
      if (status && status !== 'all') {
        queryParams.status = status.toUpperCase();
      }
      
      if (category && category !== 'all') {
        queryParams.category = category;
      }
      
      if (search) {
        queryParams.search = search;
      }

      const response = await productAPI.get('/products', {
        params: queryParams
      });
      return response.data;
    } catch (error) {
      throw new Error(error || 'Failed to fetch products');
    }
  },

  getProductById: async (productId) => {
    try {
      const response = await productAPI.get(`/products/${productId}`);
      return response.data;
    } catch (error) {
      throw new Error(error || 'Failed to fetch product details');
    }
  },

  getProductsByCategory: async (category, params = {}) => {
    try {
      const { page = 0, size = 10 } = params;
      const response = await productAPI.get(`/products/category/${category}`, {
        params: { page, size }
      });
      return response.data;
    } catch (error) {
      throw new Error(error || 'Failed to fetch products by category');
    }
  },

  searchProducts: async (searchTerm, params = {}) => {
    try {
      const { page = 0, size = 10, category, minPrice, maxPrice } = params;
      const queryParams = { page, size, search: searchTerm };
      
      if (category) queryParams.category = category;
      if (minPrice) queryParams.minPrice = minPrice;
      if (maxPrice) queryParams.maxPrice = maxPrice;

      const response = await productAPI.get('/products/search', {
        params: queryParams
      });
      return response.data;
    } catch (error) {
      throw new Error(error || 'Failed to search products');
    }
  },

  autocompleteProducts: async (query) => {
    try {
      const response = await productAPI.get('/products/autocomplete', {
        params: { query }
      });
      return response.data;
    } catch (error) {
      // Autocomplete failure is non-fatal — return empty array
      return [];
    }
  },

  aiSearchProducts: async (query, minScore = 0.3) => {
    try {
      const response = await productAPI.get('/products/ai-search', {
        params: { query, min_score: minScore }
      });
      return response.data;
    } catch (error) {
      throw new Error(error?.response?.data?.message || error?.message || 'Failed to perform AI search');
    }
  },

  // Admin Product Operations
  approveProduct: async (productId) => {
    try {
      const response = await productAPI.post(`/products/${productId}/approve`);
      return response.data;
    } catch (error) {
      throw new Error(error || 'Failed to approve product');
    }
  },

  rejectProduct: async (productId, reason) => {
    try {
      const response = await productAPI.post(`/products/${productId}/reject`, { reason });
      return response.data;
    } catch (error) {
      throw new Error(error || 'Failed to reject product');
    }
  },

  // Report a product (authenticated; reporter is taken from the token)
  reportProduct: async (productId, reason) => {
    try {
      const response = await productAPI.post(`/products/${productId}/report`, { reason });
      return response.data;
    } catch (error) {
      throw new Error(error || 'Failed to submit report');
    }
  },

  getPendingProducts: async (params = {}) => {
    try {
      const { page = 0, size = 10 } = params;
      const response = await productAPI.get('/products/pending', {
        params: { page, size }
      });
      return response.data;
    } catch (error) {
      throw new Error(error || 'Failed to fetch pending products');
    }
  },

  // Seller Product Operations
  getSellerProducts: async (sellerId, params = {}) => {
    try {
      const { page = 0, size = 10, status } = params;
      const queryParams = { page, size };
      
      if (status && status !== 'all') {
        queryParams.status = status.toUpperCase();
      }

      const response = await productAPI.get(`/products/seller/${sellerId}`, {
        params: queryParams
      });
      return response.data;
    } catch (error) {
      throw new Error(error || 'Failed to fetch seller products');
    }
  },

  createProduct: async (productData) => {
    try {
      const response = await productAPI.post('/products', productData);
      return response.data;
    } catch (error) {
      throw new Error(error || 'Failed to create product');
    }
  },

  updateProduct: async (productId, productData) => {
    try {
      const response = await productAPI.put(`/products/${productId}`, productData);
      return response.data;
    } catch (error) {
      throw new Error(error || 'Failed to update product');
    }
  },

  deleteProduct: async (productId) => {
    try {
      const response = await productAPI.delete(`/products/${productId}`);
      return response.data;
    } catch (error) {
      throw new Error(error || 'Failed to delete product');
    }
  },

  // Product Analytics
  getTopSellingProducts: async (limit = 10) => {
    try {
      const response = await productAPI.get(`/products/top-selling?limit=${limit}`);
      return response.data;
    } catch (error) {
      throw new Error(error || 'Failed to fetch top selling products');
    }
  },

  getMostViewedProducts: async (limit = 10) => {
    try {
      const response = await productAPI.get(`/products/most-viewed?limit=${limit}`);
      return response.data;
    } catch (error) {
      throw new Error(error || 'Failed to fetch most viewed products');
    }
  },

  getRecentlyAddedProducts: async (limit = 10) => {
    try {
      const response = await productAPI.get(`/products/recent?limit=${limit}`);
      return response.data;
    } catch (error) {
      throw new Error(error || 'Failed to fetch recently added products');
    }
  },

  // Product Categories
  getCategories: async () => {
    try {
      const response = await productAPI.get('/products/categories');
      return response.data;
    } catch (error) {
      throw new Error(error || 'Failed to fetch product categories');
    }
  },

  // Product Images
  uploadProductImage: async (productId, imageFile) => {
    try {
      const formData = new FormData();
      formData.append('image', imageFile);
      
      const response = await productAPI.post(`/products/${productId}/images`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      throw new Error(error || 'Failed to upload product image');
    }
  },

  deleteProductImage: async (productId, imageUrl) => {
    try {
      const response = await productAPI.delete(`/products/${productId}/images`, {
        data: { imageUrl }
      });
      return response.data;
    } catch (error) {
      throw new Error(error || 'Failed to delete product image');
    }
  },

  // Product Statistics
  getProductStatistics: async (productId) => {
    try {
      const response = await productAPI.get(`/products/${productId}/statistics`);
      return response.data;
    } catch (error) {
      throw new Error(error || 'Failed to fetch product statistics');
    }
  },

  getProductStatisticsBatch: async (productIds) => {
    try {
      // Send productIds as multiple query parameters: ?productIds=id1&productIds=id2
      const params = new URLSearchParams();
      productIds.forEach(id => params.append('productIds', id));
      const response = await productAPI.get(`/products/statistics/batch?${params.toString()}`);
      return response.data;
    } catch (error) {
      throw new Error(error || 'Failed to fetch product statistics batch');
    }
  }
};

export default productService;